/**
 * services/api.ts — typed wrappers for every API endpoint.
 *
 * Centralising every URL in one file means refactoring the API surface is a
 * grep-and-edit job, not an archaeology dig. Endpoints requiring the
 * encrypted envelope go through the shared axios instance, which already
 * handles request wrapping + response decryption via interceptors.
 */

import { api, getJson, postJson } from './axios';
import type {
	ApiEnvelope,
	Bank,
	BankDetailResponse,
	BankListResponse,
	BankLoadsResponse,
	BankSubmissionResponse,
	BranchDetailResponse,
	BranchesFilters,
	BranchesResponse,
	DataQualityFilters,
	DataQualityResponse,
	DepositFilters,
	DepositsResponse,
	LoanFilters,
	LoansResponse,
	LoginResponse,
	NPAFilters,
	NPAResponse,
	OverviewResponse,
	ProductDetailResponse,
	ProductsFilters,
	ProductsResponse,
	ProductType,
	User,
} from '../type/api';

export const API_PATHS = {
	LOGIN: 'auth/login',
	REFRESH: 'auth/refresh',
	LOGOUT: 'auth/logout',
	ME: 'me',
	HEALTH: 'health',

	BANKS: 'banks',
	BANK_SUBMISSION_STATUS: 'banks/submission-status',
	BANK_LOADS: 'banks',  // append /:orgId/loads

	// Phase 2 — analytics
	DASH_OVERVIEW: 'dashboard/overview',
	DASH_LOANS: 'dashboard/loans',
	DASH_DEPOSITS: 'dashboard/deposits',
	DASH_NPA: 'dashboard/npa',
	DASH_PRODUCTS: 'dashboard/products',
	DASH_BRANCHES: 'dashboard/branches',
	DASH_DATA_QUALITY: 'dashboard/data-quality',
	DASH_BANK_LIST: 'dashboard/banks',             // collection (directory page)
	DASH_BANK_DETAIL: 'dashboard/banks',           // append /:orgId
	DASH_BRANCH_DETAIL: 'dashboard/branches',      // append /:orgId/:branchIdLocal
	DASH_PRODUCT_DETAIL: 'dashboard/products',     // append /:productCode
} as const;

// ── Auth ────────────────────────────────────────────────────────────────

/**
 * Login — plain JSON in, plain JSON out (the client doesn't have a
 * secretKey yet, and won't until this call returns one).
 */
export async function login(
	username: string,
	password: string,
): Promise<ApiEnvelope<LoginResponse>> {
	const res = await api.post<ApiEnvelope<LoginResponse>>(API_PATHS.LOGIN, {
		username,
		password,
	});
	return res.data;
}

/** Logout — best-effort revoke. The reducer clears local state regardless. */
export async function logout(): Promise<void> {
	try {
		await postJson<void>(API_PATHS.LOGOUT, { at: Date.now() });
	} catch {
		// Swallow — we're logging out either way.
	}
}

/** GET /me — current user info. */
export const me = (): Promise<User> => getJson<User>(API_PATHS.ME);

// ── Banks ───────────────────────────────────────────────────────────────

export const getBanks = (): Promise<{ items: Bank[] }> =>
	getJson<{ items: Bank[] }>(API_PATHS.BANKS);

export const getBankSubmissionStatus = (): Promise<BankSubmissionResponse> =>
	getJson<BankSubmissionResponse>(API_PATHS.BANK_SUBMISSION_STATUS);

export const getBankLoads = (
	orgId: number,
	from?: string,
	to?: string,
): Promise<BankLoadsResponse> => {
	const qs: string[] = [];
	if (from) qs.push(`from=${encodeURIComponent(from)}`);
	if (to) qs.push(`to=${encodeURIComponent(to)}`);
	const suffix = qs.length ? `?${qs.join('&')}` : '';
	return getJson<BankLoadsResponse>(`${API_PATHS.BANK_LOADS}/${orgId}/loads${suffix}`);
};

// ── Phase 2: Dashboard analytics ────────────────────────────────────────

function buildQs(parts: Array<[string, string | number | undefined | null]>): string {
	const kv: string[] = [];
	for (const [k, v] of parts) {
		if (v === undefined || v === null || v === '') continue;
		kv.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
	}
	return kv.length ? `?${kv.join('&')}` : '';
}

function csv(ids: number[] | undefined | null): string | undefined {
	if (!ids || ids.length === 0) return undefined;
	return ids.join(',');
}

export const getOverview = (date?: string): Promise<OverviewResponse> => {
	const qs = buildQs([['date', date]]);
	return getJson<OverviewResponse>(`${API_PATHS.DASH_OVERVIEW}${qs}`);
};

export const getLoans = (filters: LoanFilters = {}): Promise<LoansResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['orgIds', csv(filters.orgIds)],
	]);
	return getJson<LoansResponse>(`${API_PATHS.DASH_LOANS}${qs}`);
};

export const getDeposits = (filters: DepositFilters = {}): Promise<DepositsResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['orgIds', csv(filters.orgIds)],
	]);
	return getJson<DepositsResponse>(`${API_PATHS.DASH_DEPOSITS}${qs}`);
};

export const getNPA = (filters: NPAFilters = {}): Promise<NPAResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['orgIds', csv(filters.orgIds)],
		['npaCodes', filters.npaCodes && filters.npaCodes.length ? filters.npaCodes.join(',') : undefined],
	]);
	return getJson<NPAResponse>(`${API_PATHS.DASH_NPA}${qs}`);
};

export const getProducts = (filters: ProductsFilters = {}): Promise<ProductsResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['productType', filters.productType ?? undefined],
		['orgIds', csv(filters.orgIds)],
		['search', filters.search],
	]);
	return getJson<ProductsResponse>(`${API_PATHS.DASH_PRODUCTS}${qs}`);
};

export const getBranches = (filters: BranchesFilters = {}): Promise<BranchesResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['orgIds', csv(filters.orgIds)],
		['sort', filters.sort],
		['limit', filters.limit],
		['search', filters.search],
	]);
	return getJson<BranchesResponse>(`${API_PATHS.DASH_BRANCHES}${qs}`);
};

export const getDataQuality = (
	filters: DataQualityFilters = {},
): Promise<DataQualityResponse> => {
	const qs = buildQs([
		['date', filters.date],
		['orgIds', csv(filters.orgIds)],
	]);
	return getJson<DataQualityResponse>(`${API_PATHS.DASH_DATA_QUALITY}${qs}`);
};

export const getBankList = (date?: string): Promise<BankListResponse> => {
	const qs = buildQs([['date', date]]);
	return getJson<BankListResponse>(`${API_PATHS.DASH_BANK_LIST}${qs}`);
};

export const getBankDetail = (orgId: number, date?: string): Promise<BankDetailResponse> => {
	const qs = buildQs([['date', date]]);
	return getJson<BankDetailResponse>(`${API_PATHS.DASH_BANK_DETAIL}/${orgId}${qs}`);
};

export const getBranchDetail = (
	orgId: number,
	branchIdLocal: string,
	date?: string,
): Promise<BranchDetailResponse> => {
	const qs = buildQs([['date', date]]);
	return getJson<BranchDetailResponse>(
		`${API_PATHS.DASH_BRANCH_DETAIL}/${orgId}/${encodeURIComponent(branchIdLocal)}${qs}`,
	);
};

export const getProductDetail = (
	productCode: string,
	type: ProductType,
	date?: string,
	orgIds?: number[],
): Promise<ProductDetailResponse> => {
	const qs = buildQs([
		['type', type],
		['date', date],
		['orgIds', csv(orgIds)],
	]);
	return getJson<ProductDetailResponse>(
		`${API_PATHS.DASH_PRODUCT_DETAIL}/${encodeURIComponent(productCode)}${qs}`,
	);
};
